<template>
  <div>
    <div class="px-4 sm:px-0">
      <PageTitle
        :title="$gettext('Paramètres')"
        :description="$gettext('Cette page décrit les paramètres globaux du moteur de calcul de risques ainsi que les seuils utilisés pour définir le niveau de risque global et par règle. Ces seuils ne sont modifiables que par votre account manager ; les paramètres d\'extraction de documents, en bas de page, sont directement modifiables par vos soins.')" />
    </div>
  </div>
  <div class="px-4 sm:px-0">
      <div v-if="organization">
        <h4 class="mt-4 text-base/7 font-semibold text-gray-900">{{ organization.attributes.name }}</h4>

        <div class="mt-6 max-w-md">
          <label class="block text-sm font-medium text-gray-900">
            {{ $gettext("Adresse e-mail du responsable AML de l'agence") }}
          </label>
          <div class="mt-2 flex items-center gap-2">
            <input
              v-model="amlOfficerEmail"
              type="email"
              class="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-primary"
              placeholder="aml.officer@example.com"
            />
            <button
              type="button"
              :disabled="!amlOfficerEmailValid || amlOfficerEmailSaving || amlOfficerEmail === organization.attributes.amlOfficerEmail"
              class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
              @click="saveAmlOfficerEmail"
            >
              {{ $gettext("Enregistrer") }}
            </button>
          </div>
          <p v-if="amlOfficerEmailError" class="mt-1 text-xs text-red-600">{{ amlOfficerEmailError }}</p>
          <p v-else-if="amlOfficerEmailSavedAt" class="mt-1 text-xs text-green-600">{{ $gettext("Enregistré.") }}</p>
        </div>
      </div>
  </div>

  <div class="mt-8 px-4 sm:px-0">
    <ExtractionSettingsCard />
  </div>

  <div v-if="organization" class="mt-8 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div class="relative">
            <table class="min-w-full table-fixed divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Entité") }}</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Seuil élevé") }}</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Seuil standard") }}</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Seuil bas") }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ entityLabels["company"] }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.companyHighThreshold }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.companyMediumThreshold }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.companyLowThreshold }}
                  </td>
                </tr>
                                <tr>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ entityLabels["physical_person"] }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.physicalPersonHighThreshold }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.physicalPersonMediumThreshold }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.physicalPersonLowThreshold }}
                  </td>
                </tr>
                                <tr>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ entityLabels["rental_listing_agreement"] }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.rentalListingAgreementHighThreshold }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.rentalListingAgreementMediumThreshold }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.rentalListingAgreementLowThreshold }}
                  </td>
                </tr>
                                <tr>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ entityLabels["sale_listing_agreement"] }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.saleListingAgreementHighThreshold }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.saleListingAgreementMediumThreshold }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organization.attributes.saleListingAgreementLowThreshold }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-8 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div class="relative">
            <table class="min-w-full table-fixed divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Nom de la règle") }}</th>
                  <th scope="col" class="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900">{{ $gettext("Entité") }}</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Seuil élevé") }}</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Seuil standard") }}</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Seuil bas") }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="organizationRiskScoringRule in organizationRiskScoringRuleStore.sortedOrganizationRiskScoringRules" :key="organizationRiskScoringRule.id" :class="[selectedOrganizationRiskScoringRules.includes(organizationRiskScoringRule.id) && 'bg-gray-50']">
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ rulesLabels[organizationRiskScoringRule.relationships.riskScoringRule.data.attributes.name] || organizationRiskScoringRule.relationships.riskScoringRule.data.attributes.name}}
                  </td>
                  <td class="whitespace-nowrap py-4 pr-3 text-sm font-medium">
                    {{ entityLabels[organizationRiskScoringRule.relationships.riskScoringRule.data.attributes.entityType] }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organizationRiskScoringRule.relationships.riskScoringRule.data.attributes.highThreshold }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organizationRiskScoringRule.relationships.riskScoringRule.data.attributes.mediumThreshold }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ organizationRiskScoringRule.relationships.riskScoringRule.data.attributes.lowThreshold }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
</template>

<script setup>
import PageTitle from "@/components/layout/PageTitle.vue";
import ExtractionSettingsCard from "@/components/settings/ExtractionSettingsCard.vue";
import { useOrganizationRiskScoringRuleStore } from "@/stores/organizationRiskScoringRule.js";
import { ref, computed, onMounted, watch } from "vue";
import { useGettext } from "vue3-gettext";
import { useOrganizationStore } from "@/stores/organization.js";

const { $gettext } = useGettext();

const organizationStore = useOrganizationStore();
const organization = ref(null);
const selectedOrganizationRiskScoringRules = ref([]);
const organizationRiskScoringRuleStore = useOrganizationRiskScoringRuleStore();

const amlOfficerEmail = ref("");
const amlOfficerEmailSaving = ref(false);
const amlOfficerEmailError = ref("");
const amlOfficerEmailSavedAt = ref(null);

const amlOfficerEmailValid = computed(() =>
  amlOfficerEmail.value === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(amlOfficerEmail.value)
);

watch(organization, (org) => {
  if (org) amlOfficerEmail.value = org.attributes.amlOfficerEmail || "";
});

const saveAmlOfficerEmail = async () => {
  amlOfficerEmailError.value = "";
  amlOfficerEmailSavedAt.value = null;
  amlOfficerEmailSaving.value = true;
  try {
    await organizationStore.update(
      organization.value.id,
      { amlOfficerEmail: amlOfficerEmail.value },
      (data) => { organization.value = data; amlOfficerEmailSavedAt.value = Date.now(); },
      (errors) => { amlOfficerEmailError.value = errors?.value?.amlOfficerEmail || $gettext("Erreur lors de l'enregistrement."); }
    );
  } finally {
    amlOfficerEmailSaving.value = false;
  }
};

const entityLabels = {
  company: $gettext("Organisation"),
  physical_person: $gettext("Personne physique"),
  rental_listing_agreement: $gettext("Dossier de location"),
  sale_listing_agreement: $gettext("Dossier de vente")
};

const rulesLabels = {
  address_country: $gettext("Risque pays"),
  establishment_date: $gettext("Ancienneté de l'organisation"),
  politicaly_exposed_person: $gettext("Personne politiquement exposée"),
  belgian_sanctions: $gettext("Sanctions belges"),
  european_sanctions: $gettext("Sanctions européennes"),
  representative_sanctions: $gettext("Sanctions parmi les représentants"),
  ultimate_beneficiary_owner_sanctions: $gettext("Sanctions parmi les UBO"),
  company_type: $gettext("Type d'organisation"),
  industry_sector: $gettext("Secteur d'activité"),
  sellers: $gettext("Risques sur les vendeurs"),
  landlords: $gettext("Risques sur les bailleurs"),
  asset_valuation: $gettext("Importance du patrimoine"),
  identification_type: $gettext("Moyen d'identification"),
  nationality_country: $gettext("Risque nationalité")
};

onMounted(async () => {
  await organizationRiskScoringRuleStore.fetchOrganizationRiskScoringRules();
  selectedOrganizationRiskScoringRules.value = organizationRiskScoringRuleStore.sortedOrganizationRiskScoringRules;
  let organizationId = selectedOrganizationRiskScoringRules.value[0].attributes.organizationId;
  organization.value = await organizationStore.fetchOne(organizationId);
});

</script>
